<template>
  <div id="genres">
    <BaseArchiveTemplate>
      <template v-if="!selectedGenres.length" #sub-nav>
        <div class="discover-heading">
          {{ getHeaderTitle }}
        </div>
      </template>
      <template v-else #sub-nav>
        <BaseFilterButton v-if="isGenresQuery"
                          v-b-toggle="genresListCollapse"
                          :state="genresListCollapseIsShown"
                          :collapse-id="genresListCollapse"/>
        <BaseFilterButton v-if="isSubjectsQuery"
                          v-b-toggle="subjectsListCollapse"
                          :state="subjectsListCollapseIsShown"
                          :collapse-id="subjectsListCollapse"/>
        <div class="selected-genres">
          <button v-for="selectedGenre in selectedGenres"
                  :key="selectedGenre.id"
                  type="button"
                  class="selected-genres__btn"
                  @click="updateSelectedGenres(selectedGenre)"
          >
            {{ selectedGenre.name }}
            <IconClose/>
          </button>
          <button v-if="selectedGenres.length > 1" type="button"
                  class="selected-genres__btn--trans"
                  @click="clearAllGenres()"
          >
            CLEAR ALL
            <IconClose/>
          </button>
        </div>
        <BaseInfoButton v-if="isGenreDescription"
                        v-b-toggle="genresInfoCollapse" :collapse-id="genresInfoCollapse"/>
      </template>
      <template v-if="!pageHasNoData" #grid>
        <EpisodeCard
          v-for="(episode, index) in filteredEpisodes"
          :key="`genres-${episode.id}-${index}`"
          :episode="episode"
          :artist="episode.mainArtist"
          :artist-name="true"
        />
        <div class="episode"/>
        <div class="episode"/>
        <div class="episode"/>
      </template>
      <template v-if="loading" #loader>
        <Loading color="black"/>
      </template>
      <template v-if="pageHasNoData" #not-found>
        <NotFoundFlag msg="Episodes"/>
      </template>
    </BaseArchiveTemplate>
    <template v-if="filteredEpisodes.length">
      <IntersectionObserver
        v-if="!loading && !noMoreDataToLoad"
        :init-observer="initObserver"
        @intersect="loadMore"
      />
    </template>
    <router-view/>
  </div>
</template>

<script>
import BaseArchiveTemplate from "@/components/BaseArchiveTemplate";
import BaseFilterButton from "@/components/ui/BaseFilterButton";
import BaseInfoButton from "@/components/ui/BaseInfoButton";
import EpisodeCard from "@/components/cards/EpisodeCard";
import Loading from "@/components/ui/Loading";
import IntersectionObserver from "@/components/ui/IntersectionObserver";
import IconClose from "@/components/svg/IconClose";
import NotFoundFlag from "@/components/errorHandlers/NotFoundFlag";
import {mapState} from 'vuex';
import axios from "axios";

export default {
  name: "DiscoverGenres",
  components: {
    BaseArchiveTemplate,
    BaseFilterButton,
    BaseInfoButton,
    IconClose,
    EpisodeCard,
    Loading,
    IntersectionObserver,
    NotFoundFlag
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from.name !== to.name && vm.$store.getters.getSelectedGenres.length === 0) {
        vm.openGenresList();
        vm.initObserver = true;
      }
    })
  },

  beforeRouteLeave(to, from, next) {
    this.closeGenresList();
    this.closeGenresInfo();
    next();
  },

  data: () => ({
    genresListCollapse: 'genres-index-collapse',
    subjectsListCollapse: 'subjects-index-collapse',
    genresInfoCollapse: 'genres-info-collapse',
    genresListCollapseIsShown: false,
    subjectsListCollapseIsShown: false,
    genresInfoCollapseIsShown: false,
    page: 0,
    pageSize: 12,
    totalPages: null,
    filteredEpisodes: [],
    loading: false,
    initObserver: false,
    episodesResponseCount: null,
    localSelectedGenresIds: [],
    queriedFromDifferentRoute: false
  }),

  computed: {
    ...mapState({
      selectedGenres: state => state.selectedGenres,
      isGenreSelectionChanged(state) {
        const isArraySubset = (a, b) => { // both params accept type number
          return a.sort((prev, next) => next - prev).toString() === b.sort((prev, next) => next - prev).toString();
        }
        return isArraySubset(this.localSelectedGenresIds, state.selectedGenres.map(genre => genre.id));
      },
      isGenreDescription() {
        return this.selectedGenres.some((genre) => genre.description.length);
      }
    }),
    isGenresQuery() {
      return this.$route.query['q'] === 'genres';
    },
    isSubjectsQuery() {
      return this.$route.query['q'] === 'subjects';
    },
    getHeaderTitle() {
      return this.isGenresQuery ? 'Discover episodes through genres and sub genres' : 'Discover episodes through various subjects and topics';
    },
    noMoreDataToLoad() {
      return this.episodesResponseCount < 15;
    },
    pageHasNoData() {
      return this.filteredEpisodes.length === 0 && this.loading === false && this.selectedGenres.length > 0;
    },
  },
  watch: {
    '$route': {
      deep: true,
        handler(newRoute, oldRoute) {
          if (typeof newRoute.query['tag'] === 'undefined' && typeof oldRoute.query['q'] !== 'undefined') {
            this.closeGenresList();
            this.openGenresList();
          }
        }
      },

      selectedGenres(newValue, oldValue) { // genres list opens when user removes on genre from the previous state
        if (newValue.length === 0) {
          this.openGenresList();
        } else {
          this.applyFiltersAndGetEpisodes();
        }
      }
    },

    mounted() {
      if (this.selectedGenres.length) {
        this.applyFiltersAndGetEpisodes();
      }
      this.$store.dispatch('setGenresList');
      this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
        if (collapseId === this.genresListCollapse) {
          this.genresListCollapseIsShown = isJustShown;
          this.closeGenresInfo();
        }
        if (collapseId === this.subjectsListCollapse) {
          this.subjectsListCollapseIsShown = isJustShown;
          this.closeGenresInfo();
        }
      });
      this.$root.$on('bv::collapse::state', (collapseId, isJustShown) => {
        if (collapseId === this.genresInfoCollapse) {
          this.genresInfoCollapseIsShown = isJustShown;
        }
      });
    },
    methods: {
      applyFiltersAndGetEpisodes() {
        this.localSelectedGenresIds = this.$store.getters.getSelectedGenres.map(e => e.id); // return new array of genre IDs from vuex and store it as local property
        this.page = 0;
        this.filteredEpisodes = [];
        this.getEpisodesByTagId();
        this.closeGenresList();
        this.closeGenresInfo();
        this.scrollToTop();
      },
      loadMore() {
        this.page++;
        this.getEpisodesByTagId();
      },

      getEpisodesByTagId() {
        this.loading = true;
        const params = this.selectedGenres.map((genre) => genre.id);

        axios.post(`${this.$router.options.api}/tags/search-episodes-by-tags-and-subtags?page=${this.page}`, params).then((response) => {
          this.episodesResponseCount = response.data.length;
          this.filteredEpisodes = [...this.filteredEpisodes, ...response.data];
          this.loading = false;
        }).catch((error) => console.error(error));
      },

      openGenresList() {
        if (!this.genresListCollapseIsShown && this.isGenresQuery) {
          // eslint-disable-next-line vue/custom-event-name-casing
          this.$root.$emit('bv::toggle::collapse', this.genresListCollapse);
        }
        if (!this.subjectsListCollapseIsShown && this.isSubjectsQuery) {
          // eslint-disable-next-line vue/custom-event-name-casing
          this.$root.$emit('bv::toggle::collapse', this.subjectsListCollapse);
        }
      },

      closeGenresList() {
        if (this.genresListCollapseIsShown) {
          // eslint-disable-next-line vue/custom-event-name-casing
          this.$root.$emit('bv::toggle::collapse', this.genresListCollapse);
        }
        if (this.subjectsListCollapseIsShown) {
          // eslint-disable-next-line vue/custom-event-name-casing
          this.$root.$emit('bv::toggle::collapse', this.subjectsListCollapse);
        }
      },

      closeGenresInfo() {
        if (this.genresInfoCollapseIsShown) {
          // eslint-disable-next-line vue/custom-event-name-casing
          this.$root.$emit('bv::toggle::collapse', this.genresInfoCollapse);
        }
      },

      updateSelectedGenres(selectedGenre) {
        const filteredGenresSelection = this.selectedGenres.filter((genre) => genre.id !== selectedGenre.id);
        this.$store.commit('setGenres', filteredGenresSelection);
      },

      clearAllGenres() {
        this.$store.commit('setGenres', []);
        this.openGenresList();
      },

      scrollToTop() {
        setTimeout(() => {
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }, 500);
      }

    }
}
</script>

<style scoped lang="scss">
@import "@/assets/sass/abstracts/_variables.scss";

.base-circle-btn {
  position: absolute;
  background-color: $c_episode;
  color: $c_black;
  border-radius: 50%;
  text-transform: uppercase;
  @include font-10-md;
  //margin-left: auto;
  width: 36px;
  height: 36px;
  z-index: 4;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  flex-shrink: 0;
  border: 1px solid $c_episode;

  &:hover {
    background-color: $c_base;
    color: $c_white;
  }
}

.selected-genres {
  display: flex;
  margin: 0 10px;
  overflow-x: auto;
  flex: 0 0 calc(50% - 72px);
  //flex: 1;
  @include media-breakpoint-down(lg) {
    margin: 0 -20px 0 10px;
  }
  @include media-breakpoint-down(md) {
    flex: 1 1 calc(100% - 66px);
  }

  &::-webkit-scrollbar {
    display: none;
  }

  &__btn {
    background-color: $c_white;
    border: 1px solid $c_white !important;
    color: $c_black;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 5px;
    @include font-10-md;
    text-transform: uppercase;
    padding: 10px;
    flex: 0 0 auto;

    &:last-child {
      margin-right: 0;
    }

    &--trans {
      @extend .selected-genres__btn;
      background-color: transparent;
      border: 1px solid $c_grey_border !important;
    }

    &:hover {
      @include media-breakpoint-up(md) {
        background-color: $c_base;
        color: $c_white;
        border: 1px solid $c_base !important;
      }
    }

    svg {
      width: 16px;
      height: 16px;
      margin-left: 10px;
    }
  }
}

</style>
